import React from "react";
import { toast } from "sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import type { Currency, Money } from "@workspace/accounting";
import { HttpRequestError } from "@workspace/base-api/fetchJson";
import type {
	MenuCategory,
	Product,
	ProductIngredient,
} from "@workspace/product-catalog-types";
import { genid } from "@workspace/shared/utils";
import { useVenueSettings } from "@workspace/venue-management";
import type { StaffMember } from "@workspace/venue-management-staff";

import { eposApi } from "../../api";
import type {
	CloseReason,
	EposData,
	Order,
	OrderItem,
	Payment,
	SelectedOptionGroup,
	SplitDefinitionInput,
} from "../../types";
import { compedItemIds } from "../comp";
import { createThenAdopt } from "../create-then-adopt";
import {
	optionRequiredOf,
	retrySend,
	unmarkSent,
	type OptionRequired,
} from "../option-required";
import {
	appliedServiceCharge,
	orderTotals,
	withServerCharge,
	type ServiceChargeSettings,
} from "../order-tax";
import {
	NOTHING_TO_RESTORE_MESSAGE,
	problemCodeOf,
	SENT_ITEM_REMOVE_FORBIDDEN_MESSAGE,
	UNDO_TOAST_MESSAGE,
	UNDO_TOAST_MS,
} from "../removal";
import { linesToSend, sendIdFor } from "../stock-warnings";
import { newOrderId } from "./order-id";
import {
	OrderWriteQueue,
	type LineSyncStatus,
	type OrderWrite,
	type RestoreSyncStatus,
	type WriteOutcome,
} from "./order-write-queue";
import { withSentRouting } from "./send-routing";
import type { EposTab } from "./types";
import { useCardPayments, type CardCancelResult } from "./use-card-payment";
import { useRecoverCardPayments } from "./use-recover-card-payments";

export { orderTotals };

/**
 * HA-768 / D-HA-24 24-2 — a failing line write surfaces within about 10 seconds: two attempts,
 * each cut off at WRITE_TIMEOUT_MS (see eposApi), half a second apart. React Query used to retry
 * these ten times with backoff, which held the order's later writes for minutes.
 *
 * <p>networkMode "always": offline, React Query would otherwise pause the mutation until it
 * reconnected, and the write queue could not tell a pause from a slow server. The queue owns
 * the offline carve-out instead (see OrderWriteQueue).
 */
const LINE_WRITE_RETRY = {
	retry: 1,
	retryDelay: 500,
	networkMode: "always",
} as const;

/** Puts a server copy of an order's lines together with the lines the server doesn't have yet. */
function mergeLines(server: OrderItem[], local: OrderItem[]) {
	const serverIds = new Set(server.map((i) => i.id));
	return [...local.filter((i) => !serverIds.has(i.id)), ...server];
}

export type OpenOrderData = Pick<
	Order,
	| "id"
	| "change"
	| "status"
	| "items"
	| "tableId"
	| "subtotal"
	| "total"
	| "tax"
	| "outstanding"
	| "totalPaid"
	| "payments"
	| "firedCourseIds"
	| "pendingCourseFire"
	| "isFrozen"
	| "activeSplitGroup"
	| "serviceCharge"
	| "serviceChargeAmount"
	| "serviceChargeRemoved"
	| "lastRemoved"
	| "comps"
	| "fullyComped"
	| "completedByComps"
> & {
	readOnly: boolean;
	type: "Split" | "Tab" | "Standard" | string;
};

// Types for the reducer
interface OrdersState {
	orderMap: Map<string, Order[] | null>;
	recentOrders: EposData["orders"];
	activeOrderId: string | null;
	isReceiptModalOpen: boolean;

	openOrderData: OpenOrderData | null;
}

export type PaymentMethods = "cash" | "card" | "mobile" | "gift-card" | "other";

interface PaymentState {
	paymentMethod: PaymentMethods;
	tipAmount: number;
	tipPercentage: number | null;
}

// HA-607 — local draft state used during split-bill configuration. Once the
// user taps Continue, this gets translated into a SplitDefinitionInput and
// POSTed via initiateSplit. The persisted shape (Order.activeSplitGroup) is
// the source of truth from there; `splitMethod`/etc. just shape the request.
interface SplitState {
	splitMethod: "none" | "items" | "percentage" | "number" | "amount";
	splitCount: number;
	splitPercentages: number[];
	splitItems: Record<string, number>;
}

type OrdersAction =
	| { type: "SET_ACTIVE_ORDER"; payload: string | null }
	| { type: "SET_OPEN_ORDER_DATA"; payload: OrdersState["openOrderData"] }
	| { type: "UPDATE_ORDER"; payload: Order }
	| {
			type: "SET_ORDER_CHARGE";
			payload: { orderId: string; server: Partial<Order> };
	  }
	| { type: "SET_RECENT_ORDERS"; payload: EposData["orders"] }
	| { type: "TOGGLE_RECEIPT_MODAL"; payload?: boolean }
	| { type: "INITIALIZE_ORDERS"; payload: Order[] };

type PaymentAction =
	| { type: "SET_PAYMENT_METHOD"; payload: PaymentState["paymentMethod"] }
	| { type: "SET_TIP_AMOUNT"; payload: number }
	| { type: "SET_TIP_PERCENTAGE"; payload: number | null };

type SplitAction =
	| { type: "SET_SPLIT_METHOD"; payload: SplitState["splitMethod"] }
	| { type: "SET_SPLIT_COUNT"; payload: number }
	| { type: "SET_SPLIT_PERCENTAGES"; payload: number[] }
	| { type: "SET_SPLIT_ITEMS"; payload: Record<string, number> };

// Reducers
function ordersReducer(state: OrdersState, action: OrdersAction): OrdersState {
	switch (action.type) {
		case "INITIALIZE_ORDERS": {
			const orderMap = action.payload.reduce((map, order) => {
				const staffId = order.server.id;
				if (!map.has(staffId)) {
					map.set(staffId, []);
				}
				map.get(staffId)?.push(order);
				return map;
			}, new Map<string, Order[]>());

			// Preserve openOrderData if the active order still exists in the refreshed data
			const activeStillExists = state.openOrderData
				? action.payload.some((o) => o.id === state.openOrderData!.id)
				: false;

			return {
				...state,
				orderMap,
				openOrderData: activeStillExists ? state.openOrderData : null,
			};
		}
		case "SET_ACTIVE_ORDER": {
			const id = action.payload;

			if (!id) {
				return {
					...state,
					activeOrderId: null,
					openOrderData: null,
				};
			}

			const order = [...state.orderMap.values()]
				.flat()
				.find((o) => o?.id === id);

			if (!order) {
				return state;
			}

			return {
				...state,
				activeOrderId: id,
				openOrderData: {
					...order,
					readOnly: false,
					type: "Standard",
				},
			};
		}
		case "SET_OPEN_ORDER_DATA":
			return { ...state, openOrderData: action.payload };
		case "SET_ORDER_CHARGE": {
			// HA-357 slice C — the server's copy of a new order gives the till its charge (the snapshot, and whether it's off).
			const { orderId, server } = action.payload;
			const newMap = new Map<string, Order[] | null>();
			for (const [key, list] of state.orderMap) {
				newMap.set(
					key,
					list?.map((o) =>
						o.id === orderId ? withServerCharge(o, server) : o,
					) ?? null,
				);
			}
			const openOrderData =
				state.openOrderData?.id === orderId
					? withServerCharge(state.openOrderData, server)
					: state.openOrderData;
			return { ...state, orderMap: newMap, openOrderData };
		}
		case "UPDATE_ORDER": {
			const updatedOrder = action.payload;
			const newMap = new Map(state.orderMap);
			let serverOrders = newMap.get(updatedOrder.server.id || "");

			if (!serverOrders) {
				newMap.set(updatedOrder.server.id || "", []);
				serverOrders = [];
			}

			newMap.set(updatedOrder.server.id || "", [
				updatedOrder,
				...serverOrders.filter((_) => _.id !== updatedOrder.id),
			]);

			// Sync openOrderData when the active order is updated: its items (the server stamps queueId on them after an
			// add), and the money and the charge the server's figures carry (HA-357). Its own fields (type, readOnly) stay.
			const openOrderData =
				state.openOrderData?.id === updatedOrder.id
					? {
							...state.openOrderData,
							items: updatedOrder.items,
							subtotal: updatedOrder.subtotal,
							tax: updatedOrder.tax,
							total: updatedOrder.total,
							outstanding:
								updatedOrder.outstanding ?? state.openOrderData.outstanding,
							serviceCharge:
								updatedOrder.serviceCharge ?? state.openOrderData.serviceCharge,
							serviceChargeRemoved:
								updatedOrder.serviceChargeRemoved ??
								state.openOrderData.serviceChargeRemoved,
							serviceChargeAmount:
								updatedOrder.serviceChargeAmount ??
								state.openOrderData.serviceChargeAmount,
							// HA-265: null is the server saying nothing can be restored; undefined is a local update that has no word on it.
							lastRemoved:
								updatedOrder.lastRemoved !== undefined
									? updatedOrder.lastRemoved
									: state.openOrderData.lastRemoved,
							// HA-658: the server's comps. A comp's answer also says whether the comps Completed the order (or Remove Comp
							// reopened it), so the status follows then and only then.
							comps: updatedOrder.comps ?? state.openOrderData.comps,
							fullyComped:
								updatedOrder.fullyComped ?? state.openOrderData.fullyComped,
							completedByComps:
								updatedOrder.completedByComps ??
								state.openOrderData.completedByComps,
							...(updatedOrder.completedByComps !== undefined
								? { status: updatedOrder.status }
								: {}),
						}
					: state.openOrderData;

			return { ...state, orderMap: newMap, openOrderData };
		}
		case "SET_RECENT_ORDERS":
			return { ...state, recentOrders: action.payload };
		case "TOGGLE_RECEIPT_MODAL":
			return {
				...state,
				isReceiptModalOpen: action.payload ?? !state.isReceiptModalOpen,
			};
		default:
			return state;
	}
}

function paymentReducer(
	state: PaymentState,
	action: PaymentAction,
): PaymentState {
	switch (action.type) {
		case "SET_PAYMENT_METHOD":
			return { ...state, paymentMethod: action.payload };
		case "SET_TIP_AMOUNT":
			return { ...state, tipAmount: action.payload };
		case "SET_TIP_PERCENTAGE":
			return { ...state, tipPercentage: action.payload };
		default:
			return state;
	}
}

function splitReducer(state: SplitState, action: SplitAction): SplitState {
	switch (action.type) {
		case "SET_SPLIT_METHOD":
			return {
				...defaultSplitState,
				splitMethod: action.payload,
			};
		case "SET_SPLIT_COUNT":
			return { ...state, splitCount: action.payload };
		case "SET_SPLIT_PERCENTAGES":
			return { ...state, splitPercentages: action.payload };
		case "SET_SPLIT_ITEMS":
			return { ...state, splitItems: action.payload };
		default:
			return state;
	}
}

/** HA-849 — the server's figures, when a response carries them, over the till's own. */
export function withServerFigures(order: Order): Order {
	const figures = order.figures;
	if (!figures) return order;
	return {
		...order,
		// HA-885: the server has answered, so the figures the till was holding back as stale are current again.
		...(order.figuresStale ? { figuresStale: false } : {}),
		subtotal: figures.subtotal,
		tax: figures.tax.value,
		total: figures.total,
		outstanding: figures.outstanding,
		// HA-357: the charge is in the total; keep the amount the server gave, so the VAT split can take it out.
		serviceChargeAmount: figures.serviceCharge ?? order.serviceChargeAmount,
	};
}

/** HA-849 — complete-order's refusal of an order the server's recorded payments haven't paid. */
export const ORDER_NOT_PAID_CODE = "ORDER_NOT_PAID";

export function isOrderNotPaid(error: unknown): error is HttpRequestError {
	return (
		error instanceof HttpRequestError &&
		error.problem?.code === ORDER_NOT_PAID_CODE
	);
}

/**
 * HA-849 — the server completes an order only once its own recorded payments cover it. A card
 * payment is recorded by the card saga a moment after the reader succeeds, so a complete sent
 * straight after can find it not yet there: ask again, briefly, and only for that refusal.
 */
export async function completeWhenPaid<T>(
	complete: () => Promise<T>,
	attempts = 5,
	delayMs = 800,
): Promise<T> {
	for (let attempt = 1; ; attempt++) {
		try {
			return await complete();
		} catch (error) {
			if (attempt >= attempts || !isOrderNotPaid(error)) throw error;
			await new Promise((resolve) => setTimeout(resolve, delayMs));
		}
	}
}

// Helper hooks
function useOrderCalculations() {
	return React.useMemo(
		() => ({
			calculateOrderTotals: (
				items: OrderItem[],
				settings: EposData["settings"],
				serviceCharge?: ServiceChargeSettings | null,
				comped?: ReadonlySet<string>,
			) => orderTotals(items, settings.taxRate, serviceCharge, comped),
		}),
		[],
	);
}

function useOrderMutations(
	tenant: string,
	venueId: string,
	onUpdateOrder: (order: Order) => void,
	getCurrentOrder: () => Order | undefined,
	// HA-768 — writes now finish after the operator may have moved to another order, so a response
	// is merged into the order it is for, never into whichever order is open.
	getOrder: (orderId: string) => Order | undefined,
) {
	const postOrderMutation = useMutation({
		...eposApi.postOrderOptions<Partial<Order>>(tenant, venueId),
		// HA-768 — no onMutate: createNewOrder shows the order itself, and a Retry re-running this
		// would otherwise put the order back as it was when first created, without its lines.
		onSuccess(d) {
			if (!d.success) {
				console.log(d);
			}
		},
		onError(...args) {
			console.log("Error posting order", args);
		},
		// HA-768 — retried by pushOrder instead, which first checks whether a failed create landed:
		// a blind retry of one that did is refused by the server as a duplicate.
		...LINE_WRITE_RETRY,
		retry: false,
	});

	const addItemToOrderMutation = useMutation({
		...eposApi.addItemToOrderOptions(tenant, venueId),
		onSuccess(d, variables) {
			if (!d.success) {
				console.log(d);
				return;
			}
			const updated = d.response;
			const order = getOrder(variables.orderId);

			if (order) {
				onUpdateOrder(
					withServerFigures({
						...order,
						...updated,
						items: mergeLines(updated.items, order.items),
					}),
				);
			}
		},
		onError(...rest) {
			console.log("Error adding item to order", rest);
		},
		...LINE_WRITE_RETRY,
	});

	const moveItemToCourseMutation = useMutation({
		...eposApi.moveItemToCourseOptions(tenant, venueId),
		onSuccess(d) {
			if (!d.success) {
				console.log(d);
				return;
			}
			const updated = d.response;
			const currentOrder = getCurrentOrder();

			if (currentOrder) {
				onUpdateOrder(
					withServerFigures({
						...currentOrder,
						...updated,
						items: [
							...currentOrder.items.filter(
								(x) => !updated.items.some((i: OrderItem) => i.id === x.id),
							),
							...updated.items,
						],
					}),
				);
			}
		},
		onError(...rest) {
			console.log("Error moving item to course", rest);
		},
	});

	const removeItemToOrderMutation = useMutation({
		...eposApi.removeItemToOrderOptions(tenant, venueId),
		// HA-768 / D-HA-24 — no onMutate: the line goes when the server has removed it. Removing it
		// first is how a refused remove quietly came back on the next poll.
		onSuccess(d, variables) {
			if (!d.success) {
				console.log(d);
				return;
			}
			const updated = d.response;
			const order = getOrder(variables.orderId);
			if (order) {
				onUpdateOrder(
					withServerFigures({
						...order,
						...updated,
						// HA-265: the server's word on what can be restored (it omits it when nothing can).
						lastRemoved: updated.lastRemoved ?? null,
						items: mergeLines(
							updated.items,
							order.items.filter((i) => i.id !== variables.id),
						),
					}),
				);
			}
		},
		onError() {
			console.log("Error removing item to order");
		},
		...LINE_WRITE_RETRY,
	});

	const completeOrderMutation = useMutation({
		...eposApi.completeOrderOptions(tenant, venueId),
		// HA-767 — no onMutate: nothing about a completion is shown before the server has it.
		// HA-777 — and no onSuccess reading a response: complete-order answers 204, with no body.
		// completeOrder applies the completion itself once this resolves.
		// HA-767 — completeOrder now waits on this, so a failure is the caller's to show, and a
		// retry would only lengthen a wait the operator is watching. One attempt: a retry after a
		// lost response would also be refused by a server that had already completed the order.
		retry: false,
	});

	const closeOrderMutation = useMutation({
		...eposApi.closeOrderOptions(tenant, venueId),
	});

	const dismissOrderMutation = useMutation({
		...eposApi.dismissOrderOptions(tenant, venueId),
	});

	const serviceChargeMutation = useMutation({
		...eposApi.serviceChargeOptions(tenant, venueId),
	});

	const sendToKitchenMutation = useMutation({
		...eposApi.sendToKitchenOptions(tenant, venueId),
		onMutate(variables) {
			const currentOrder = getCurrentOrder();
			const markedIds: string[] = [];
			if (currentOrder) {
				const sentIds = new Set(variables.itemIds);
				onUpdateOrder({
					...currentOrder,
					items: currentOrder.items.map((i) => {
						if (
							(!variables.itemIds || sentIds.has(i.id)) &&
							!i.sentToKitchen &&
							i.queueId
						) {
							markedIds.push(i.id);
							return { ...i, sentToKitchen: true };
						}
						return i;
					}),
				});
			}
			return { markedIds };
		},
		onSuccess(d) {
			if (!d.success) {
				console.log(d);
			}
			// Don't overwrite local state — the optimistic update in onMutate already marked the
			// items sent, and re-merging here would clobber anything added since the request left.
			// HA-619: the response now also carries the warnings, read at the call site.
			//
			// HA-600-2 — but the sent lines do take where they went and their prep time from the
			// answer: the server decides both at send, so the till can't know them before.
			const currentOrder = getCurrentOrder();
			if (currentOrder && currentOrder.id === d.response.order.id) {
				onUpdateOrder({
					...currentOrder,
					items: withSentRouting(currentOrder.items, d.response.order.items),
				});
			}
		},
		onError(_error, _variables, context) {
			console.log("Error sending to kitchen");
			// HA-877: a refused send sent nothing, so the lines the optimistic update marked sent go back to
			// unsent, or the till would show as sent what the kitchen never got.
			const currentOrder = getCurrentOrder();
			if (currentOrder && context?.markedIds.length) {
				onUpdateOrder({
					...currentOrder,
					items: unmarkSent(currentOrder.items, context.markedIds),
				});
			}
		},
		// HA-877: a connection or server fault is retried; a refusal (a 400, such as an unanswered required option
		// group) is final and is not asked again.
		retry: retrySend,
	});

	const resendTicketsMutation = useMutation({
		...eposApi.resendTicketsOptions(tenant, venueId),
		onError(...rest) {
			console.log("Error resending tickets", rest);
		},
	});

	const printReceiptMutation = useMutation({
		...eposApi.printReceiptOptions(tenant, venueId),
		onError(...rest) {
			console.log("Error printing receipt", rest);
		},
	});

	// HA-360 Part B — the Tab bill or Tab receipt.
	const printTabMutation = useMutation(
		eposApi.printTabOptions(tenant, venueId),
	);

	const removeUnsentItemsMutation = useMutation({
		...eposApi.removeUnsentItemsOptions(tenant, venueId),
		onError(...rest) {
			console.log("Error removing unsent items", rest);
		},
	});

	// HA-265 — Undo / Restore last removed item. The line comes back when the server has put it back, at the position the
	// server gives it (no onMutate, as for a remove).
	const restoreItemMutation = useMutation({
		...eposApi.restoreItemOptions(tenant, venueId),
		onSuccess(d, variables) {
			if (!d.success) {
				console.log(d);
				return;
			}
			const updated = d.response;
			const order = getOrder(variables.orderId);
			if (order) {
				const serverIds = new Set(updated.items.map((i) => i.id));
				onUpdateOrder(
					withServerFigures({
						...order,
						...updated,
						lastRemoved: updated.lastRemoved ?? null,
						// The server's order, with its lines in the server's order, then any line it doesn't have yet.
						items: [
							...updated.items,
							...order.items.filter((i) => !serverIds.has(i.id)),
						],
					}),
				);
			}
		},
		// No onError: a refused or failed restore reaches the screen through the write queue (restoreRemoved: Not restored, a Retry, and a
		// toast with the server's words), not a console line (HA-979).
	});

	const saveOrderMutation = useMutation({
		...eposApi.saveOrderOptions(tenant, venueId),
		onSuccess(d, variables) {
			if (!d.success) {
				console.log(d);
				return;
			}
			const updated = d.response;
			const order = getOrder(variables.orderId);

			if (order) {
				onUpdateOrder(
					withServerFigures({
						...order,
						...updated,
						items: mergeLines(updated.items, order.items),
					}),
				);
			}
		},
		onError(...rest) {
			console.log("Error saving order", rest);
		},
	});

	// HA-607 — split-bill backend wiring.
	const initiateSplitMutation = useMutation({
		...eposApi.initiateSplitOptions(tenant, venueId),
		onSettled(_data, _error, _variables, _onMutateResult, context) {
			context.client.invalidateQueries({ queryKey: [tenant, venueId, "epos"] });
		},
	});

	const cancelSplitMutation = useMutation({
		...eposApi.cancelSplitOptions(tenant, venueId),
		onSettled(_data, _error, _variables, _onMutateResult, context) {
			context.client.invalidateQueries({ queryKey: [tenant, venueId, "epos"] });
		},
	});

	const recordCashPaymentMutation = useMutation({
		...eposApi.recordCashPaymentOptions(tenant, venueId),
		onSettled(_data, _error, _variables, _onMutateResult, context) {
			context.client.invalidateQueries({ queryKey: [tenant, venueId, "epos"] });
		},
	});

	// HA-658 slice 4 — Comp and Remove Comp are server-confirmed (D-HA-24): no onMutate, so nothing shows as comped until the server
	// answers with the order, and its comps and figures replace the till's. The server's lines lead; any line it doesn't have yet
	// stays (mergeLines).
	const mergeComp = (
		d: { success: boolean; response: Order },
		orderId: string,
	) => {
		if (!d.success) return;
		const order = getOrder(orderId);
		if (!order) return;
		onUpdateOrder(
			withServerFigures({
				...order,
				...d.response,
				items: mergeLines(d.response.items, order.items),
			}),
		);
	};
	const compItemMutation = useMutation({
		...eposApi.compItemOptions(tenant, venueId),
		onSuccess: (d, v) => mergeComp(d, v.orderId),
	});
	const removeItemCompMutation = useMutation({
		...eposApi.removeItemCompOptions(tenant, venueId),
		onSuccess: (d, v) => mergeComp(d, v.orderId),
	});
	const compOrderMutation = useMutation({
		...eposApi.compOrderOptions(tenant, venueId),
		onSuccess: (d, v) => mergeComp(d, v.orderId),
	});
	const removeOrderCompMutation = useMutation({
		...eposApi.removeOrderCompOptions(tenant, venueId),
		onSuccess: (d, v) => mergeComp(d, v.orderId),
	});

	return {
		compItemMutation,
		removeItemCompMutation,
		compOrderMutation,
		removeOrderCompMutation,
		postOrderMutation,
		addItemToOrderMutation,
		moveItemToCourseMutation,
		removeItemToOrderMutation,
		restoreItemMutation,
		completeOrderMutation,
		closeOrderMutation,
		dismissOrderMutation,
		serviceChargeMutation,
		sendToKitchenMutation,
		saveOrderMutation,
		resendTicketsMutation,
		printReceiptMutation,
		printTabMutation,
		removeUnsentItemsMutation,
		initiateSplitMutation,
		cancelSplitMutation,
		recordCashPaymentMutation,
	};
}

/** HA-1025 — what the options popup chose for the line(s) of a tap, carried onto them as they are added. */
export interface NewLineOptions {
	selectedOptions: SelectedOptionGroup[];
	notes: string;
	removedIngredients: ProductIngredient[];
}

export function useOrders({
	selectedStaff,
	settings,
	tabs,
	defaultCurrency,
	data,
	setActiveTabId,
	setSelectedCategory,
	venueId,
	zero,
	tenant,
	setCurrentUserId,
	setView,
	deviceId,
}: {
	selectedStaff: StaffMember | null;
	settings: EposData["settings"];
	setView: (view: "products" | "payment" | "settings" | "staff") => void;
	tabs: EposTab[];
	defaultCurrency: Currency;
	defaultCurrencyCode: string;
	data: EposData;
	setActiveTabId: (orderId: string | null) => void;
	setSelectedCategory: (category: MenuCategory | null) => void;
	venueId: string;
	zero: Money;
	tenant: string;
	setCurrentUserId: (id: string | null | undefined) => void;
	deviceId: string | null;
}) {
	// HA-768 / D-HA-24 — one write queue per order: see OrderWriteQueue. It replaces a single
	// terminal-wide queue, behind which one order's failing create held every other order's writes.
	const queueRef = React.useRef<OrderWriteQueue | null>(null);
	queueRef.current ??= new OrderWriteQueue();
	const queue = queueRef.current;

	// Re-render when a line's sync status changes.
	React.useSyncExternalStore(queue.subscribe, queue.getVersion);

	// The D-HA-24 offline carve-out: what an outage paused is sent when the device reconnects.
	React.useEffect(() => {
		const online = () => queue.online();
		window.addEventListener("online", online);
		return () => window.removeEventListener("online", online);
	}, [queue]);

	const queryClient = useQueryClient();
	const { paymentProvider } = useVenueSettings();

	// Initialize reducers
	const [ordersState, ordersDispatch] = React.useReducer(ordersReducer, {
		orderMap: new Map<string, Order[] | null>(),
		recentOrders: data.orders,
		activeOrderId: null,
		isReceiptModalOpen: false,
		openOrderData: null,
	});

	const [paymentState, paymentDispatch] = React.useReducer(paymentReducer, {
		paymentMethod: "cash" as const,
		tipAmount: 0,
		tipPercentage: null,
	});

	const [splitState, splitDispatch] = React.useReducer(
		splitReducer,
		defaultSplitState,
	);

	// Initialize orders on mount, and from every poll. HA-768 — the server's copy lacks whatever
	// this terminal has not sent yet, so those lines (and orders created offline) are put back, or
	// a poll would wipe a line marked Sending or Not saved from under its Retry.
	React.useEffect(() => {
		const serverIds = new Set(data.orders.map((o) => o.id));
		const payload = [
			...data.orders.map((o) => {
				const pending = queue.pendingLines(o.id);
				return pending.length
					? { ...o, items: mergeLines(o.items, pending) }
					: o;
			}),
			...queue.unsentOrders().filter((o) => !serverIds.has(o.id)),
		];

		ordersDispatch({ type: "INITIALIZE_ORDERS", payload });
	}, [data.orders, queue]);

	// Derived state
	const mapOrders: Order[][] = [...ordersState.orderMap.values()].filter(
		(o) => o?.length,
	) as Order[][];

	const orders: Order[] = mapOrders
		.filter((o) => o.length)
		.reduce(
			(acc, list) => acc.concat(list.filter((x: Order | null) => !!x)),
			[] as Order[],
		);

	const currentOrder = orders.find(
		(order) => order.id === ordersState.activeOrderId,
	);

	React.useEffect(() => {
		if (currentOrder && ordersState.openOrderData?.id !== currentOrder.id) {
			ordersDispatch({
				type: "SET_OPEN_ORDER_DATA",
				payload: {
					...currentOrder,
					readOnly: false,
					type: "Standard",
				},
			});
		}
	}, [currentOrder, ordersState.openOrderData?.id]);

	// Get helper functions and mutations
	const { calculateOrderTotals } = useOrderCalculations();
	const updateOrderHandler = React.useCallback((order: Order) => {
		ordersDispatch({ type: "UPDATE_ORDER", payload: order });
	}, []);

	const getCurrentOrder = React.useCallback(() => currentOrder, [currentOrder]);

	// Read through a ref: a write's response arrives renders after it was sent.
	const ordersRef = React.useRef(orders);
	ordersRef.current = orders;
	const getOrder = React.useCallback(
		(orderId: string) => ordersRef.current.find((o) => o.id === orderId),
		[],
	);

	const {
		compItemMutation,
		removeItemCompMutation,
		compOrderMutation,
		removeOrderCompMutation,
		postOrderMutation,
		addItemToOrderMutation,
		moveItemToCourseMutation,
		removeItemToOrderMutation,
		restoreItemMutation,
		completeOrderMutation,
		closeOrderMutation,
		dismissOrderMutation,
		serviceChargeMutation,
		sendToKitchenMutation,
		saveOrderMutation,
		resendTicketsMutation,
		printReceiptMutation,
		printTabMutation,
		removeUnsentItemsMutation,
		initiateSplitMutation,
		cancelSplitMutation,
		recordCashPaymentMutation,
	} = useOrderMutations(
		tenant,
		venueId,
		updateOrderHandler,
		getCurrentOrder,
		getOrder,
	);

	/**
	 * HA-768 / D-HA-24 24-1 — a line write the operator doesn't wait on (an add, an edit, a remove)
	 * reports its own failure: the line shows Not saved, and this says so, with a Retry. The result
	 * is returned too, for a caller that does wait (the options editor stays open on a failure).
	 */
	const reportWriteFailure = (
		orderId: string,
		error: unknown,
		write?: Pick<OrderWrite, "kind" | "item">,
	) => {
		const name = write?.item?.name ?? "the item";
		toast.error(
			write?.kind === "remove"
				? `Couldn't remove ${name}`
				: write?.kind === "restore"
					? "Couldn't restore the item"
					: `Couldn't save ${name}`,
			{
				id: `order-write-${orderId}`,
				description: error instanceof Error ? error.message : undefined,
				action: {
					label: "Retry",
					onClick: () => void retryWrites(orderId),
				},
			},
		);
	};

	/**
	 * HA-768 — Retry, from the toast or a line's badge. A Retry that fails again says so, with
	 * another Retry: it used to be swallowed, leaving the line Not saved and the operator unsure
	 * whether the tap did anything.
	 */
	const retryWrites = (orderId: string): Promise<WriteOutcome> => {
		// Read before retrying: the toast names the write that failed again, and its line.
		const write = queue.failedWrite(orderId);
		const retried = queue.retry(orderId);
		retried.catch((error: unknown) =>
			reportWriteFailure(orderId, error, write),
		);
		return retried;
	};

	const sendLine = (write: OrderWrite): Promise<WriteOutcome> => {
		const sent = queue.enqueue(write);
		sent.catch((error: unknown) =>
			reportWriteFailure(write.orderId, error, write),
		);
		return sent;
	};

	/** Drops a line locally, with the totals it carried: nothing was ever saved for it. */
	const dropLine = (orderId: string, itemId: string) => {
		const order = getOrder(orderId);
		if (!order) return;

		const items = order.items.filter((i) => i.id !== itemId);
		const { subtotal, tax, total, serviceChargeAmount } = calculateOrderTotals(
			items,
			settings,
			appliedServiceCharge(order),
			compedItemIds(order),
		);
		const updatedOrder: Order = {
			...order,
			items,
			subtotal: { ...order.subtotal, value: subtotal },
			tax,
			total: { ...order.total, value: total },
			serviceChargeAmount: { ...order.total, value: serviceChargeAmount },
			outstanding: { ...(order.outstanding ?? zero), value: total },
		};

		ordersDispatch({ type: "UPDATE_ORDER", payload: updatedOrder });
	};

	/**
	 * HA-588 — `returnedToStock` is the operator's answer to "Returned to stock?", asked only for a
	 * line that was already sent. Undefined and false both mean nothing goes back, which is right:
	 * a line that never sent took nothing out, and food that was made and binned is gone.
	 */
	const removeItem = async (
		orderId: string,
		itemId: string,
		returnedToStock?: boolean,
	) => {
		const order = getOrder(orderId);
		const item = order?.items.find((i) => i.id === itemId);
		if (!order || !item) return;

		// HA-768 / D-HA-24 — a line the server may not have yet (Sending, Not saved, Not yet sent)
		// is dropped from the queue. If none of its writes ever reached the server, that is the whole
		// removal; if one did, a remove still goes, because it may have landed.
		if (queue.lineStatus(orderId, itemId)) {
			const { mayExist } = queue.discard(orderId, itemId);

			if (!mayExist) {
				dropLine(orderId, itemId);
				return;
			}
		}

		// HA-265: one removalId per removal, minted here and kept on a Retry (the run below is what Retry runs again), so a replay is
		// one removal on the server (and one reversal of stock).
		const removalId = genid();
		const removal = { refused: false };

		// The line stays, marked Removing, until the server has removed it; the response drops it.
		const outcome = await sendLine({
			orderId,
			kind: "remove",
			itemId,
			item,
			run: () =>
				removeItemToOrderMutation
					.mutateAsync({
						orderId,
						id: itemId,
						returnedToStock,
						removalId,
					} as unknown as Record<string, unknown> & {
						orderId: string;
						id: string;
						returnedToStock?: boolean;
						removalId?: string;
					})
					.catch((error: unknown) => {
						// AC3, the server's side: a sent line needs `epos:tabs:manage`. A retry would be refused the same way, so say so
						// and leave the line (the queue is not held for a Retry that cannot work).
						if (problemCodeOf(error) !== "SENT_ITEM_REMOVE_FORBIDDEN")
							throw error;
						removal.refused = true;
						toast.error(SENT_ITEM_REMOVE_FORBIDDEN_MESSAGE);
					}),
		});

		// AC5: the removal is confirmed, so offer the Undo for five seconds.
		if (outcome === "sent" && !removal.refused) {
			toast(UNDO_TOAST_MESSAGE, {
				id: `removed-${orderId}`,
				duration: UNDO_TOAST_MS,
				action: {
					label: "Undo",
					onClick: () =>
						void restoreRemoved(orderId, removalId).catch(() => {}),
				},
			});
		}
	};

	/**
	 * HA-265 (AC6, AC7) — puts the most recent removal back: the toast's Undo, or Restore last removed item. The line returns, at
	 * the server's position, once the server has put it back; until then the order shows Restoring…, Not restored (with a Retry)
	 * or, offline, Not yet sent. NOTHING_TO_RESTORE (a newer removal, or the order moved on) is said once and does not hold the queue.
	 */
	const restoreRemoved = async (orderId: string, removalId: string) => {
		await sendLine({
			orderId,
			kind: "restore",
			run: () =>
				restoreItemMutation
					.mutateAsync({ orderId, removalId })
					.catch((error: unknown) => {
						const code = problemCodeOf(error);
						if (code === "NOTHING_TO_RESTORE") {
							toast.error(NOTHING_TO_RESTORE_MESSAGE);
							return;
						}
						if (code === "SENT_ITEM_REMOVE_FORBIDDEN") {
							toast.error(SENT_ITEM_REMOVE_FORBIDDEN_MESSAGE);
							return;
						}
						throw error;
					}),
		});
	};

	/**
	 * HA-658 slice 4 — Comp, Remove Comp: through the order's write queue, so each lands after the order's own pending writes (a
	 * line the server doesn't have yet can't be comped), and server-confirmed: they resolve once the server has answered and reject
	 * with its refusal, which the caller tells (compRefusalMessage). Nothing is shown as comped before then.
	 */
	const compWrite = <T,>(orderId: string, run: () => Promise<T>) =>
		queue.enqueue({ orderId, kind: "order", run });

	const compItem = (orderId: string, itemId: string, reason: string) =>
		compWrite(orderId, () =>
			compItemMutation.mutateAsync({ orderId, itemId, reason }),
		);

	const removeItemComp = (orderId: string, itemId: string) =>
		compWrite(orderId, () =>
			removeItemCompMutation.mutateAsync({ orderId, itemId }),
		);

	const compOrder = (orderId: string, reason: string) =>
		compWrite(orderId, () =>
			compOrderMutation.mutateAsync({ orderId, reason }),
		);

	const removeOrderComp = (orderId: string) =>
		compWrite(orderId, () => removeOrderCompMutation.mutateAsync({ orderId }));

	const updateItemOptions = (
		orderId: string,
		itemId: string,
		selectedOptions: SelectedOptionGroup[],
		notes: string,
		removedIngredients: ProductIngredient[],
	) => {
		if (!currentOrder) return;

		const updatedOrder = { ...currentOrder };
		const itemIndex = updatedOrder.items.findIndex((i) => i.id === itemId);

		if (itemIndex === -1) {
			return;
		}

		// Update the item with new options and note
		updatedOrder.items[itemIndex] = {
			...updatedOrder.items[itemIndex]!,
			selectedOptions,
			removedIngredients,
			notes,
		};

		// Recalculate totals (options might have price impacts)
		const { subtotal, tax, total, serviceChargeAmount } = calculateOrderTotals(
			updatedOrder.items,
			settings,
			appliedServiceCharge(updatedOrder),
			compedItemIds(updatedOrder),
		);

		updatedOrder.serviceChargeAmount = {
			...updatedOrder.total,
			value: serviceChargeAmount,
		};
		updatedOrder.total = {
			...updatedOrder.total,
			value: total,
		};
		updatedOrder.subtotal = {
			...updatedOrder.subtotal,
			value: subtotal,
		};
		updatedOrder.tax = tax;

		if (updatedOrder.outstanding) {
			updatedOrder.outstanding = {
				...updatedOrder.outstanding,
				value: total,
			};
		} else {
			updatedOrder.outstanding = {
				...zero,
				value: total,
			};
		}

		ordersDispatch({ type: "UPDATE_ORDER", payload: updatedOrder });
		ordersDispatch({
			type: "SET_OPEN_ORDER_DATA",
			payload: {
				...updatedOrder,
				readOnly: false,
				type: "Standard",
			},
		});

		// HA-768 / D-HA-24 — the edit shows at once, marked Sending, and the caller waits for the
		// server: the options editor closes on success and stays open, input kept, on a failure.
		const item = updatedOrder.items[itemIndex];

		return sendLine({
			orderId,
			kind: "edit",
			itemId,
			item,
			run: () =>
				addItemToOrderMutation.mutateAsync({
					...item,
					orderId,
				} as unknown as Record<string, unknown> & { orderId: string }),
		});
	};

	// HA-583: the completed order is captured so the post-payment receipt modal
	// can render + reprint after the active-order state is reset for staff-select.
	const [lastCompletedOrder, setLastCompletedOrder] =
		React.useState<Order | null>(null);

	const completeOrder = async (o: Order | null = null) => {
		const order = o || getCurrentOrder();
		if (!order) return;

		const updatedOrder = { ...order };
		updatedOrder.status = "Completed";
		updatedOrder.completedAt = new Date().toISOString();
		updatedOrder.updatedAt = new Date().toISOString();

		// HA-767 — wait for the server, then show Completed. This used to mark the order Completed,
		// drop its tab and navigate, then send the complete with mutate() — which returns at once —
		// so a dropped request left the order Open with its cash tenders recorded nowhere, and no
		// error. It still goes through the write queue, so it lands after the order's earlier
		// writes, but the caller now waits for the answer; a rejection leaves everything as it was.
		// HA-768 — and an order with lines the server doesn't have is refused, not completed.
		// HA-849 — the server completes on its own recorded payments (HA-554 Part 2: it takes no body), so
		// an unpaid order is refused, 409 ORDER_NOT_PAID; a card payment may take a moment to be recorded.
		await queue.enqueue({
			orderId: updatedOrder.id,
			kind: "order",
			run: () =>
				completeWhenPaid(() =>
					completeOrderMutation.mutateAsync({ orderId: updatedOrder.id }),
				).catch((error: unknown) => {
					throw isOrderNotPaid(error)
						? new Error(
								error.problem?.detail ??
									"The order isn't paid yet: take the payment first.",
							)
						: error;
				}),
		});

		// HA-777 — the server has accepted exactly this order as Completed, so this is the order to
		// show. Complete answers 204: HA-767 dispatched its (empty) body as the order, the reducer
		// threw on `{}.server.id`, and the EPOS screen went down right after a successful completion.
		const completed = updatedOrder;

		ordersDispatch({ type: "UPDATE_ORDER", payload: completed });

		// HA-554: completing an order no longer closes its tab. The tab leaves the panel when the server's ActiveTabs no
		// longer have it (a payment that settles the tab closes it there).
		if (order.tabId) setActiveTabId(null);

		// HA-583: show the receipt modal (functional Print Receipt + Close) over
		// the staff-select reset. Capture the order first — the active-order state
		// is cleared immediately below.
		setLastCompletedOrder(completed);
		ordersDispatch({ type: "TOGGLE_RECEIPT_MODAL", payload: true });

		ordersDispatch({ type: "SET_ACTIVE_ORDER", payload: null });
		setCurrentUserId(null);
		setView("staff");
	};

	const closeOrder = async (
		orderId: string,
		reason: CloseReason,
		notes?: string | null,
	) => {
		const order = getCurrentOrder();

		// HA-766 — wait for the server, then navigate; the same rule as dismissOrder (HA-764). A
		// close that was dropped or cancelled after navigating first left the order Open for the
		// next staff member to be handed, and a rejection here now keeps it on screen for the
		// caller's error toast. The close dialog's pending state covers the wait.
		await closeOrderMutation.mutateAsync({ orderId, reason, notes });

		ordersDispatch({ type: "SET_ACTIVE_ORDER", payload: null });
		setCurrentUserId(null);
		setView("staff");

		// HA-554: closing an order leaves its tab open (AC5); only Close tab or a settling payment closes it.
		if (order?.tabId) setActiveTabId(null);
	};

	// HA-357 slice C (AC9) — the server answers with the order's figures; the till takes those over its own, so the
	// running total and the charge line are the server's the moment it answers.
	const setServiceChargeRemoved = async (orderId: string, removed: boolean) => {
		const figures = await serviceChargeMutation.mutateAsync({
			orderId,
			removed,
		});
		const order = getCurrentOrder();
		if (order?.id === orderId) {
			ordersDispatch({
				type: "UPDATE_ORDER",
				payload: withServerFigures({
					...order,
					serviceChargeRemoved: removed,
					figures,
				}),
			});
		}
	};

	const dismissOrder = async (orderId: string) => {
		// HA-764 — wait for the server, then navigate. This used to clear the order and switch to
		// the staff view first, so a dismiss that was dropped or cancelled (the endpoint commits on
		// RequestAborted) left the order Open with nobody looking at it, and the next staff member
		// to log in was handed it by switchOrder. A rejection here leaves the order on screen for
		// the caller's error toast; the button's pending state covers the wait.
		await dismissOrderMutation.mutateAsync({ orderId });

		ordersDispatch({ type: "SET_ACTIVE_ORDER", payload: null });
		setCurrentUserId(null);
		setView("staff");
	};

	const sendToKitchen = async (orderId: string, itemIds?: string[]) => {
		// HA-588 — the send id is derived from the lines being sent, so a retry after a reconnect
		// carries the same one and the server deducts once.
		//
		// Those lines then go on the wire EXPLICITLY, so the id and the set the server deducts are
		// the same list by construction. Leaving itemIds undefined and letting the server resolve
		// the unsent set itself is what made this unsafe: the two answers are computed at different
		// moments from different state, so a send whose order had not loaded derived the id from an
		// empty list while the server deducted the real lines — and the retry, with the order
		// loaded, derived a different id and deducted them again.
		const sending = linesToSend(itemIds, getCurrentOrder());

		// Refuse rather than fall back to []: an empty list is indistinguishable from "this order
		// has nothing unsent", and guessing produces exactly the mismatch above.
		if (!sending) {
			throw new Error(
				`Cannot send order ${orderId}: the order is not loaded, so the lines to send are unknown.`,
			);
		}

		// Returns the result so the caller can surface HA-619 print warnings and HA-588 stock
		// refusals; a send that reaches nothing still succeeds here, and those are the only things
		// that say so.
		// HA-768 — through the order's write queue, so a line added a moment ago reaches the server
		// before the send that names it, and a line the server doesn't have refuses the send.
		let result:
			Awaited<ReturnType<typeof sendToKitchenMutation.mutateAsync>> | undefined;

		await queue.enqueue({
			orderId,
			kind: "order",
			run: async () => {
				result = await sendToKitchenMutation.mutateAsync({
					orderId,
					itemIds: sending,
					deviceId: deviceId ?? undefined,
					sendId: sendIdFor(orderId, sending),
				});
			},
		});

		return result!;
	};

	// HA-768 / D-HA-24 — wait for the server, then leave the order (HA-764's rule). This navigated
	// first, so a save that failed or never ran left the kitchen without the order and nobody on
	// the screen to say so. It lands after the order's pending writes, and a rejection keeps the
	// order on screen for the caller's error.
	const saveOrder = async (orderId: string) => {
		await queue.enqueue({
			orderId,
			kind: "order",
			run: () =>
				saveOrderMutation.mutateAsync({
					orderId,
					deviceId: deviceId ?? undefined,
				}),
		});

		// HA-1023 — the same staff member stays signed in, on a clean menu for the next order. This
		// went to the staff view with the staff member still selected, which renders nothing.
		ordersDispatch({ type: "SET_ACTIVE_ORDER", payload: null });
		setSelectedCategory(null);
		setActiveTabId(null);
		setView("products");
	};

	const resendTickets = async (orderId: string, courseId: string) =>
		await resendTicketsMutation.mutateAsync({ orderId, courseId });

	// HA-583 — print this terminal's bill/receipt for an order. Throws on the server's error responses
	// (no printer / unreachable / etc.) so callers can surface a toast; resolves with {documentType,...}.
	const printReceipt = async (orderId: string) => {
		if (!deviceId) {
			throw new Error("This terminal has no device id; cannot print.");
		}
		return await printReceiptMutation.mutateAsync({ orderId, deviceId });
	};

	// HA-360 Part B — print this terminal's Tab bill or Tab receipt (the server decides which). Throws on the server's
	// error responses, as printReceipt does.
	const printTab = async (tabId: string) => {
		if (!deviceId) {
			throw new Error("This terminal has no device id; cannot print.");
		}
		return await printTabMutation.mutateAsync({ tabId, deviceId });
	};

	const removeUnsentItems = async (orderId: string) => {
		await removeUnsentItemsMutation.mutateAsync({ orderId });
		// Server response indicates success; order data refreshed via next polling cycle.
		// If the removal emptied the order, the server will have emitted OrderDismissed
		// and the order will no longer appear in eposData.orders.
	};

	const moveItemToCourse = async (
		orderId: string,
		itemId: string,
		toCourseId: string | null,
	) => {
		await moveItemToCourseMutation.mutateAsync({
			orderId,
			itemId,
			toCourseId,
		});
	};

	/**
	 * HA-768 — the create is not safe to repeat: the server refuses an order id it already has. So
	 * a create that failed may have landed with its answer lost, and the order is looked for before
	 * the failure is believed. The terminal's own EPOS data is where: order detail needs a
	 * permission staff may not have.
	 */
	const orderExistsOnServer = async (orderId: string) => {
		// Offline, the server can't be asked, and React Query would pause this fetch until it could:
		// the create would never settle, and the queue never get to pause it as the carve-out says.
		if (!navigator.onLine) return false;

		try {
			const epos = await queryClient.fetchQuery({
				...eposApi.getEposOptions(tenant, venueId),
				staleTime: 0,
				// One quick look, or a failing create would sit behind three more of them (~22s).
				retry: false,
				networkMode: "always",
			});
			return epos.orders.some((o) => o.id === orderId);
		} catch {
			return false;
		}
	};

	// HA-357 slice C — after the create, the till takes the server's copy of the order: the venue's charge snapshot, which the
	// till's own copy doesn't have. It reads the list WITHOUT going through the query cache: a cache refresh replaces the whole
	// order map, and a read that lands before the first line is saved drops that line from the till.
	const adoptServerCharge = async (orderId: string) => {
		if (!navigator.onLine) return;
		try {
			const epos = await eposApi.getEpos(tenant, venueId);
			const server = epos.orders.find((o) => o.id === orderId);
			if (server) {
				ordersDispatch({
					type: "SET_ORDER_CHARGE",
					payload: { orderId, server },
				});
			}
		} catch {
			// The create is done; the charge reaches the till on the next line write or a reload.
		}
	};

	const pushOrder = (_venueId: string, order: Order, _isNew: boolean) => {
		return queue.enqueue({
			orderId: order.id,
			kind: "create",
			order,
			run: async () => {
				// Two attempts, as for every line write (LINE_WRITE_RETRY), each followed by the check.
				for (let attempt = 1; ; attempt++) {
					try {
						// HA-357 — the read of the charge starts after the create and is not waited on: the queue moves on at once.
						await createThenAdopt(
							async () => {
								await postOrderMutation.mutateAsync({
									...order,
									items: [],
									total: undefined,
									subtotal: undefined,
									tax: undefined,
									outstanding: undefined,
									change: undefined,
									totalPaid: undefined,
								});
							},
							() => adoptServerCharge(order.id),
						);
						return;
					} catch (error) {
						if (await orderExistsOnServer(order.id)) return;
						if (attempt >= 1 + LINE_WRITE_RETRY.retry) throw error;
						await new Promise((r) =>
							setTimeout(r, LINE_WRITE_RETRY.retryDelay),
						);
					}
				}
			},
		});
	};

	// HA-768 / D-HA-24 — not awaited by the caller: the line shows at once, marked Sending, and
	// reports its own failure (sendLine).
	const addProduct = (_venueId: string, orderId: string, item: OrderItem) => {
		void sendLine({
			orderId,
			kind: "add",
			itemId: item.id,
			item,
			run: () =>
				addItemToOrderMutation.mutateAsync({
					...item,
					orderId,
				} as unknown as Record<string, unknown> & { orderId: string }),
		}).catch(() => {});
	};

	// HA-768 — a second tap while an order is still being created must not start another one.
	const creatingOrder = React.useRef(false);
	// HA-357 — the order whose create is in flight: a tap during it adds its lines here, behind the create in its queue.
	const inFlightOrder = React.useRef<Order | null>(null);
	const [isCreatingOrder, setIsCreatingOrder] = React.useState(false);

	/** HA-357 — a product's lines onto an order: the create's own, or a tap that came in while the order was being created. */
	const addProductLinesTo = (
		order: Order,
		product: Product,
		variantId: string | undefined,
		menuId: string | undefined,
		quantity: number,
		options?: NewLineOptions,
	) => {
		const variant = product.variants?.find((v) => v.id === variantId);

		for (let i = 0; i < quantity; i++) {
			const item: OrderItem = {
				id: `ITEM-${genid()}`,
				productId: product.id,
				productVariantId: variantId!,
				menuId: menuId!,
				name: variant?.name || product.name,
				price: variant?.salePrice || product.basePrice || zero,
				quantity: 1,
				status: "Pending",
				sentToKitchen: false,
				// HA-1025 — the choices made in the options popup, on each line of the tap.
				...(options && {
					selectedOptions: options.selectedOptions,
					notes: options.notes || undefined,
					removedIngredients: options.removedIngredients,
				}),
			};

			order.items.push(item);

			const { subtotal, tax, total, serviceChargeAmount } =
				calculateOrderTotals(
					order.items,
					settings,
					appliedServiceCharge(order),
				);
			order.serviceChargeAmount = {
				...order.total,
				value: serviceChargeAmount,
			};

			order.subtotal = {
				...order.subtotal,
				value: subtotal,
			};
			order.tax = tax;
			order.total = {
				...order.total,
				value: total,
			};

			if (order.outstanding) {
				order.outstanding = {
					...order.outstanding,
					value: total,
				};
			} else {
				order.outstanding = {
					...zero,
					value: total,
				};
			}

			addProduct(venueId, order.id, item);
		}
	};

	const createNewOrder = async (
		params: {
			product?: Product;
			variantId?: string;
			menuId?: string;
			quantity?: number;
			tabId?: string;
			options?: NewLineOptions;
		} = {},
	): Promise<Order | null> => {
		const { product, variantId, menuId, quantity = 1, tabId, options } = params;

		if (!selectedStaff) return null;

		if (creatingOrder.current) {
			// HA-357 — a tap while the order is still being created: its lines join that order (the queue runs the create first).
			// Dropping them here lost the line: no order id was active to add to, so the tap was silently discarded.
			const pending = inFlightOrder.current;
			if (pending && params.product) {
				addProductLinesTo(
					pending,
					params.product,
					params.variantId,
					params.menuId,
					params.quantity ?? 1,
					params.options,
				);
			}
			return pending;
		}

		let tabName: string | undefined;

		if (tabId) {
			const tab = tabs.find((t) => t.id === tabId);
			if (tab) {
				tabName = tab.name ?? undefined;
			}
		}

		const newOrder: Order = {
			id: newOrderId(),
			tabId: tabId ?? undefined,
			status: "Open",
			items: [],
			createdAt: new Date().toISOString(),
			updatedAt: new Date().toISOString(),
			server: selectedStaff,
			payments: [],
			type: "drink",
			currency: product?.costPrice?.currency
				? product.costPrice.currency
				: defaultCurrency.code,
			subtotal: zero,
			tax: 0,
			total: zero,
			outstanding: zero,
			change: zero,
		};

		if (tabName) {
			newOrder.customer = {
				name: tabName,
				phone: "",
			};
		}

		// HA-768 / D-HA-24 — the order exists once the server has it: the first tap waits for the
		// create, and a failure leaves nothing half-made behind, just the error and a Retry of the
		// same tap. Offline, the create is recorded and the order goes on as Not yet sent.
		creatingOrder.current = true;
		inFlightOrder.current = newOrder;
		setIsCreatingOrder(true);
		try {
			await pushOrder(venueId, newOrder, true);
		} catch (error) {
			queue.abandon(newOrder.id);
			toast.error("Couldn't start the order", {
				id: "order-create",
				description: error instanceof Error ? error.message : undefined,
				action: {
					label: "Retry",
					onClick: () => void createNewOrder(params),
				},
			});
			return null;
		} finally {
			creatingOrder.current = false;
			inFlightOrder.current = null;
			setIsCreatingOrder(false);
		}

		if (product)
			addProductLinesTo(
				newOrder,
				product,
				variantId,
				menuId,
				quantity,
				options,
			);

		ordersDispatch({ type: "UPDATE_ORDER", payload: newOrder });
		ordersDispatch({ type: "SET_ACTIVE_ORDER", payload: newOrder.id });

		// HA-554: a New round on a tab; the server joins it to the tab as it creates it.
		if (tabId) setActiveTabId(tabId);

		return newOrder;
	};

	/**
	 * HA-849 — whole-order cash is recorded on the server (/cash-payments), as split cash already was,
	 * and shows as paid once it is. It used to reach the server only in the complete-order body, which
	 * the server no longer reads. Through the write queue, so it lands after the order's own writes.
	 * The tender goes as handed over; the server applies what is owed and the rest is change.
	 */
	const payCash = async (payment: Payment) => {
		const order = getCurrentOrder();
		if (!order) return;

		const tip =
			paymentState.tipAmount > 0
				? { ...payment.amount, value: paymentState.tipAmount }
				: null;

		await queue.enqueue({
			orderId: order.id,
			kind: "order",
			run: () =>
				recordCashPaymentMutation.mutateAsync({
					orderId: order.id,
					payload: { paymentId: payment.id, amount: payment.amount, tip },
				}),
		});

		processPayment("cash", payment, true);
	};

	/**
	 * `recorded` — HA-849: the server has recorded this payment (cash via /cash-payments), so it is
	 * Completed here too, paid in full or not; the till then offers Close Order, not a Dismiss the
	 * server would refuse.
	 */
	const processPayment = (
		method: PaymentMethods,
		payment: Payment,
		recorded = false,
	) => {
		const order = getCurrentOrder();
		if (!order) return;

		if (method === "card") {
			console.log("card", payment);
		}

		const updatedOrder = { ...order };

		// Attach per-tender tip if one is set
		const paymentWithTip: Payment =
			paymentState.tipAmount > 0
				? {
						...payment,
						tip: { ...zero, value: paymentState.tipAmount },
						amount: {
							...payment.amount,
							value: payment.amount.value + paymentState.tipAmount,
						},
					}
				: payment;

		updatedOrder.payments = [
			...updatedOrder.payments.filter((x) => x.id !== paymentWithTip.id),
			recorded ? { ...paymentWithTip, status: "Completed" } : paymentWithTip,
		];

		const paid = updatedOrder.payments.reduce(
			(acc, p) => acc + p.amount.value,
			0,
		);

		updatedOrder.status = paid >= updatedOrder.total.value ? "Paid" : "Open";

		if (updatedOrder.status === "Paid") {
			updatedOrder.payments = updatedOrder.payments.map((p) => ({
				...p,
				status: "Completed",
			}));
		}

		// Order-level tip is the sum of all per-tender tips
		updatedOrder.tip = updatedOrder.payments.reduce(
			(acc, p) => acc + (p.tip?.value ?? 0),
			0,
		);

		updatedOrder.outstanding = {
			...zero,
			value: Math.max(updatedOrder.total.value - paid, 0),
		};

		if (paid > updatedOrder.total.value) {
			updatedOrder.change = {
				...zero,
				value: paid - updatedOrder.total.value,
			};
		}

		updatedOrder.totalPaid = {
			...zero,
			value: paid,
		};

		updatedOrder.completedAt =
			updatedOrder.status === "Paid"
				? new Date().toISOString()
				: updatedOrder.completedAt;

		ordersDispatch({ type: "UPDATE_ORDER", payload: updatedOrder });
		ordersDispatch({
			type: "SET_OPEN_ORDER_DATA",
			payload: {
				...ordersState.openOrderData!,
				...updatedOrder,
			},
		});

		// Reset tip for next tender
		paymentDispatch({ type: "SET_TIP_AMOUNT", payload: 0 });
		paymentDispatch({ type: "SET_TIP_PERCENTAGE", payload: null });
	};

	/**
	 * HA-767 — the card payment is cancelled first (reader, then server; see useCardPayments), and
	 * the local payments are cleared only if it was. A refusal is returned, changing nothing.
	 */
	const clearPayments = async (): Promise<CardCancelResult> => {
		if (!currentOrder) return { cancelled: true };

		const cancel = await cardPayments.cancel();
		if (!cancel.cancelled) return cancel;

		const updatedOrder = { ...currentOrder };
		updatedOrder.payments = [];
		updatedOrder.status = "Open";
		updatedOrder.outstanding = {
			...(updatedOrder.outstanding || zero),
			value: updatedOrder.total.value,
		};

		updatedOrder.change = zero;
		updatedOrder.totalPaid = zero;

		updatedOrder.completedAt = undefined;

		ordersDispatch({ type: "UPDATE_ORDER", payload: updatedOrder });
		paymentDispatch({ type: "SET_PAYMENT_METHOD", payload: "cash" });

		return cancel;
	};

	// HA-768 — returns at once: the line shows marked Sending and reports its own failure.
	const addItemToOrder = (_orderId: string, item: OrderItem) => {
		if (!currentOrder) return;

		const updatedOrder = { ...currentOrder };
		const orderIndex = updatedOrder.items.findIndex((i) => i.id === item.id);

		if (orderIndex !== -1) {
			updatedOrder.items[orderIndex] = {
				...updatedOrder.items[orderIndex],
				...item,
			};
		} else {
			updatedOrder.items.push(item);
		}

		const { subtotal, tax, total, serviceChargeAmount } = calculateOrderTotals(
			updatedOrder.items,
			settings,
			appliedServiceCharge(updatedOrder),
			compedItemIds(updatedOrder),
		);

		updatedOrder.serviceChargeAmount = {
			...updatedOrder.total,
			value: serviceChargeAmount,
		};
		updatedOrder.total = {
			...updatedOrder.total,
			value: total,
		};
		updatedOrder.subtotal = {
			...updatedOrder.subtotal,
			value: subtotal,
		};
		updatedOrder.tax = tax;

		if (updatedOrder.outstanding) {
			updatedOrder.outstanding = {
				...updatedOrder.outstanding,
				value: total,
			};
		} else {
			updatedOrder.outstanding = {
				...updatedOrder.total,
				value: total,
			};
		}

		ordersDispatch({ type: "UPDATE_ORDER", payload: updatedOrder });
		ordersDispatch({
			type: "SET_OPEN_ORDER_DATA",
			payload: {
				...updatedOrder,
				readOnly: false,
				type: "Standard",
			},
		});
		addProduct(venueId, updatedOrder.id, item);
	};

	const switchOrder = (staffMember: StaffMember) => {
		if (currentOrder?.server.id === staffMember.id) return;

		const order = orders
			.filter((x) => x.status === "Open")
			.find((o) => o.server.id === staffMember.id);

		if (!!order && order.status !== "Completed") {
			if (tabs.length) {
				const tab = tabs.find((t) => t.orders.some((o) => o.id === order.id));
				if (!tab) {
					ordersDispatch({ type: "SET_ACTIVE_ORDER", payload: order.id });
					if (order.tabId) setActiveTabId(order.tabId);
				}
			} else {
				ordersDispatch({ type: "SET_ACTIVE_ORDER", payload: order.id });
				if (order.tabId) setActiveTabId(order.tabId);
			}
		} else {
			ordersDispatch({ type: "SET_ACTIVE_ORDER", payload: null });
		}
	};

	const cardPayments = useCardPayments({
		tenant,
		venueId,
	});

	// HA-886 — a card payment the till never recorded (a reload mid-payment) is recorded from the order.
	useRecoverCardPayments({
		tenant,
		venueId,
		orders,
		activePaymentId: cardPayments.paymentId,
	});

	// HA-1024 — the server's refusal of a send, a save or a payment for a line with no choice for a required group, held
	// here so whichever screen was refused (the order's buttons, or cash, card or split on the payment screen) has the
	// order screen open that line's editor with the message inside it. It was a toast, which sat over Save Changes.
	const [optionRefusal, setOptionRefusal] = React.useState<
		(OptionRequired & { orderId: string }) | null
	>(null);
	const showOptionRefusal = (orderId: string, missing: OptionRequired) => {
		const onScreen =
			ordersState.openOrderData?.id === orderId &&
			ordersState.openOrderData.items.some((i) => i.id === missing.lineId);

		// A line that isn't on screen has no editor to open: the message is still said.
		if (!onScreen) {
			toast.error(missing.detail);
			return;
		}
		setOptionRefusal({ ...missing, orderId });
	};

	// Leaving the order lets its refusal go: coming back to it later must not open the editor over nothing just done.
	const openOrderId = ordersState.openOrderData?.id;
	const [refusalSeenOn, setRefusalSeenOn] = React.useState(openOrderId);
	if (refusalSeenOn !== openOrderId) {
		setRefusalSeenOn(openOrderId);
		if (optionRefusal && optionRefusal.orderId !== openOrderId)
			setOptionRefusal(null);
	}

	// HA-768 — true while a card payment is being started, so Pay with Card can't be pressed twice.
	const [isStartingCard, setIsStartingCard] = React.useState(false);

	async function startCardPayment(
		order: Order | undefined,
		options?: { splitId?: string | null; amount?: Money },
	) {
		if (!order || isStartingCard) return;
		const bill = options?.amount ?? order.outstanding ?? order.total;
		// HA-858 (S2, AC13): a tip entered on the till rides on the card payment, which is for the bill and the tip; the
		// server then has the reader skip its own tip screen.
		const tip = paymentState.tipAmount > 0 ? paymentState.tipAmount : 0;
		const payment = {
			id: genid(),
			method: "card",
			status: "Pending",
			orderId: order.id,
			amount: tip > 0 ? { ...bill, value: bill.value + tip } : bill,
			tip: tip > 0 ? { ...bill, value: tip } : null,
			// HA-607 — when settling a specific split this rides the
			// CardPaymentStarted event so the projection flips the matching
			// split to Paid on completion.
			splitId: options?.splitId ?? null,
		} as Payment & { splitId?: string | null };

		// HA-768 / D-HA-24 — a start that failed used to vanish: this threw into an onClick nobody
		// caught, and the payment screen never changed. Now the operator stays where they are, is
		// told, and can retry (or take another method). It goes through the order's write queue, so
		// the card is started for lines the server has, and refused while any are Not saved.
		let response: Awaited<ReturnType<typeof cardPayments.start>> | undefined;
		setIsStartingCard(true);
		try {
			await queue.enqueue({
				orderId: order.id,
				kind: "order",
				run: async () => {
					response = await cardPayments.start(payment);
				},
			});
		} catch (error) {
			cardPayments.reset();
			// HA-877: a line still needs a required choice. The card is not charged; trying again can't help.
			const missing = optionRequiredOf(error);
			if (missing) {
				showOptionRefusal(order.id, missing);
				return;
			}
			toast.error("Couldn't start the card payment", {
				id: "card-start",
				description: error instanceof Error ? error.message : undefined,
				action: {
					label: "Retry",
					onClick: () => void startCardPayment(order, options),
				},
			});
			return;
		} finally {
			setIsStartingCard(false);
		}

		paymentDispatch({ type: "SET_PAYMENT_METHOD", payload: "card" });
		ordersDispatch({
			type: "UPDATE_ORDER",
			payload: {
				...order,
				payments: [
					...order.payments,
					{
						...payment,
						paymentProviderInfo: response,
					},
				],
			},
		});
	}

	return {
		orders,
		recentOrders: ordersState.recentOrders,
		setRecentOrders: (recentOrdersData: EposData["orders"]) =>
			ordersDispatch({ type: "SET_RECENT_ORDERS", payload: recentOrdersData }),
		currentOrder,
		lastCompletedOrder,
		createNewOrder,
		calculateOrderTotals: (items: OrderItem[]) =>
			calculateOrderTotals(items, settings),
		isReceiptModalOpen: ordersState.isReceiptModalOpen,
		setIsReceiptModalOpen: (open?: boolean) =>
			ordersDispatch({ type: "TOGGLE_RECEIPT_MODAL", payload: open }),
		paymentMethod: paymentState.paymentMethod,
		setPaymentMethod: (method: PaymentState["paymentMethod"]) =>
			paymentDispatch({ type: "SET_PAYMENT_METHOD", payload: method }),
		tipAmount: paymentState.tipAmount,
		setTipAmount: (amount: number) =>
			paymentDispatch({ type: "SET_TIP_AMOUNT", payload: amount }),
		tipPercentage: paymentState.tipPercentage,
		setTipPercentage: (percentage: number | null) =>
			paymentDispatch({ type: "SET_TIP_PERCENTAGE", payload: percentage }),
		openOrderData: ordersState.openOrderData,
		setOpenOrderData: (orderData: OrdersState["openOrderData"]) =>
			ordersDispatch({ type: "SET_OPEN_ORDER_DATA", payload: orderData }),
		splitMethod: splitState.splitMethod,
		setSplitMethod: (method: SplitState["splitMethod"]) =>
			splitDispatch({ type: "SET_SPLIT_METHOD", payload: method }),
		splitCount: splitState.splitCount,
		setSplitCount: (count: number) =>
			splitDispatch({ type: "SET_SPLIT_COUNT", payload: count }),
		splitPercentages: splitState.splitPercentages,
		setSplitPercentages: (percentages: number[]) =>
			splitDispatch({ type: "SET_SPLIT_PERCENTAGES", payload: percentages }),
		splitItems: splitState.splitItems,
		setSplitItems: (items: Record<string, number>) =>
			splitDispatch({ type: "SET_SPLIT_ITEMS", payload: items }),

		defaultCurrency,

		clearPayments,
		processPayment,
		payCash,
		completeOrder,
		closeOrder,
		dismissOrder,
		setServiceChargeRemoved,
		sendToKitchen,
		saveOrder,
		resendTickets,
		printReceipt,
		printTab,
		removeUnsentItems,
		moveItemToCourse,
		updateOrder: (order: Partial<Order>) =>
			ordersDispatch({ type: "UPDATE_ORDER", payload: order as Order }),

		addItemToOrder,
		removeItem,
		restoreRemoved,
		updateItemOptions,
		compItem,
		removeItemComp,
		compOrder,
		removeOrderComp,

		pushOrder,

		// HA-768 / D-HA-24 — what the screen shows for writes the server hasn't confirmed.
		lineSyncStatus: (orderId: string, itemId: string): LineSyncStatus | null =>
			queue.lineStatus(orderId, itemId),
		restoreStatus: (orderId: string): RestoreSyncStatus | null =>
			queue.restoreStatus(orderId),
		orderSyncState: (orderId: string) => queue.state(orderId),
		hasUnsentLines: (orderId: string) => queue.hasUnsent(orderId),
		retryOrderWrites: retryWrites,
		isCreatingOrder,
		isStartingCard,

		switchOrder,
		setActiveOrderId: (id: string | null) =>
			ordersDispatch({ type: "SET_ACTIVE_ORDER", payload: id }),
		activeOrderId: ordersState.activeOrderId,

		cardPayments,
		startCardPayment,
		optionRefusal,
		showOptionRefusal,
		clearOptionRefusal: () => setOptionRefusal(null),
		paymentProvider,

		// HA-607 — split-bill mutations. UI surface drives off
		// `currentOrder.activeSplitGroup` for state; these mutations push the
		// shape into the persisted order.
		initiateSplit: (orderId: string, definition: SplitDefinitionInput) =>
			initiateSplitMutation.mutateAsync({ orderId, definition }),
		cancelSplit: (orderId: string) =>
			cancelSplitMutation.mutateAsync({ orderId }),
		recordCashPayment: (
			orderId: string,
			payload: Parameters<typeof eposApi.recordCashPayment>[3],
		) => recordCashPaymentMutation.mutateAsync({ orderId, payload }),
	};
}
const defaultSplitState: SplitState = {
	splitMethod: "none" as const,
	splitCount: 2,
	splitPercentages: [50, 50],
	splitItems: {},
};
